import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompany } from '../components/Layout';
import api from '../utils/api';

const today = () => new Date().toISOString().split('T')[0];
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().split('T')[0]; };

const emptyItem = () => ({ item_name: '', description: '', unit_cost: '', quantity: '1' });

export default function InvoiceFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { activeCompany } = useCompany();

  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    currency: 'INR',
    issue_date: today(),
    due_date: addDays(today(), 30),
    tax_type: 'GST',
    tax_rate: '18',
    amount_paid: '0',
    notes: '',
    terms: 'NET 30 Days. Finance Charge of 1.5% will be made on unpaid balances after 30 days.',
  });
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!activeCompany) return;
    api.get(`/clients?company_id=${activeCompany.id}`).then(r => setClients(r.data));
    if (isEdit) {
      api.get(`/invoices/${id}`).then(r => {
        const inv = r.data;
        setForm({
          client_id: inv.client_id,
          currency: inv.currency,
          issue_date: inv.issue_date,
          due_date: inv.due_date || '',
          tax_type: inv.tax_type || 'GST',
          tax_rate: String(inv.tax_rate || 0),
          amount_paid: String(inv.amount_paid || 0),
          notes: inv.notes || '',
          terms: inv.terms || '',
        });
        setItems(inv.items.length > 0 ? inv.items.map(i => ({
          item_name: i.item_name,
          description: i.description || '',
          unit_cost: String(i.unit_cost),
          quantity: String(i.quantity),
        })) : [emptyItem()]);
      });
    }
  }, [activeCompany, id, isEdit]);

  // Auto-set currency when client selected
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    setForm(f => ({ ...f, client_id: clientId, currency: client?.currency || 'INR' }));
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setItems(items => items.map((item, idx) => idx === i ? { ...item, [k]: v } : item));
  const addItem = () => setItems(i => [...i, emptyItem()]);
  const removeItem = (i) => setItems(items => items.filter((_, idx) => idx !== i));

  // Calculate totals
  const subtotal = items.reduce((s, item) => {
    const v = parseFloat(item.unit_cost || 0) * parseFloat(item.quantity || 0);
    return s + (isNaN(v) ? 0 : v);
  }, 0);
  const isINR = form.currency === 'INR';
  const taxRate = isINR ? parseFloat(form.tax_rate || 0) : 0;
  const taxAmount = isINR ? (subtotal * taxRate / 100) : 0;
  const total = subtotal + taxAmount;
  const amountPaid = parseFloat(form.amount_paid || 0);
  const balanceDue = total - amountPaid;

  const fmt = (n) => n.toFixed(2);
  const sym = isINR ? '₹' : '$';

  const handleSave = async (status = 'draft') => {
    if (!form.client_id) return setError('Please select a client');
    if (items.some(i => !i.item_name || !i.unit_cost)) return setError('All items need a name and unit cost');
    setSaving(true); setError('');
    const payload = {
      ...form,
      company_id: activeCompany.id,
      items: items.map(i => ({ ...i, unit_cost: parseFloat(i.unit_cost), quantity: parseFloat(i.quantity) })),
      status,
    };
    try {
      if (isEdit) {
        await api.put(`/invoices/${id}`, payload);
      } else {
        await api.post('/invoices', payload);
      }
      navigate('/invoices');
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>{isEdit ? 'Edit Invoice' : 'New Invoice'}</h2>
          <p>{activeCompany?.name}</p>
        </div>
        <button className="btn btn-outline" onClick={() => navigate('/invoices')}>← Back</button>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, marginBottom: 18, fontSize: 14 }}>{error}</div>}

      <div className="card">
        <h4 style={{ marginBottom: 16, fontSize: 15, fontWeight: 600, borderBottom: '1px solid #f0f0f0', paddingBottom: 12 }}>Invoice Details</h4>

        <div className="form-grid form-grid-2" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label>Client *</label>
            <select className="form-control" value={form.client_id} onChange={e => handleClientChange(e.target.value)}>
              <option value="">— Select Client —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.contact_person ? ` (${c.contact_person})` : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Currency</label>
            <div className="currency-toggle">
              <button type="button" className={form.currency === 'INR' ? 'active-inr' : ''} onClick={() => setF('currency', 'INR')}>₹ INR (India)</button>
              <button type="button" className={form.currency === 'USD' ? 'active-usd' : ''} onClick={() => setF('currency', 'USD')}>$ USD (International)</button>
            </div>
          </div>
        </div>

        <div className="form-grid form-grid-3" style={{ marginBottom: 16 }}>
          <div className="form-group">
            <label>Issue Date</label>
            <input className="form-control" type="date" value={form.issue_date} onChange={e => setF('issue_date', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Due Date</label>
            <input className="form-control" type="date" value={form.due_date} onChange={e => setF('due_date', e.target.value)} />
          </div>
          {isINR && (
            <div className="form-group">
              <label>GST Rate (%)</label>
              <select className="form-control" value={form.tax_rate} onChange={e => setF('tax_rate', e.target.value)}>
                <option value="0">0% (Exempt)</option>
                <option value="5">5% GST</option>
                <option value="12">12% GST</option>
                <option value="18">18% GST</option>
                <option value="28">28% GST</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Line Items */}
      <div className="card">
        <h4 style={{ marginBottom: 16, fontSize: 15, fontWeight: 600 }}>Line Items</h4>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1.3fr 0.8fr 1.2fr 32px', gap: 8, marginBottom: 8 }}>
          {['Item', 'Description', 'Unit Cost', 'Qty', 'Price', ''].map((h, i) => (
            <div key={i} style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', padding: '0 4px' }}>{h}</div>
          ))}
        </div>

        {items.map((item, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1.3fr 0.8fr 1.2fr 32px', gap: 8, marginBottom: 8, alignItems: 'start' }}>
            <input className="form-control" style={{ fontSize: 13 }} placeholder="e.g. Web Updates" value={item.item_name} onChange={e => setItem(idx, 'item_name', e.target.value)} />
            <textarea className="form-control" style={{ fontSize: 13, resize: 'vertical', minHeight: '38px' }} placeholder="Brief description..." value={item.description} onChange={e => setItem(idx, 'description', e.target.value)} rows={2} />
            <input className="form-control" style={{ fontSize: 13 }} type="number" min="0" step="0.01" placeholder="0.00" value={item.unit_cost} onChange={e => setItem(idx, 'unit_cost', e.target.value)} />
            <input className="form-control" style={{ fontSize: 13 }} type="number" min="1" step="1" value={item.quantity} onChange={e => setItem(idx, 'quantity', e.target.value)} />
            <div style={{ padding: '9px 12px', fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>
              {sym}{fmt((parseFloat(item.unit_cost || 0) * parseFloat(item.quantity || 0)))}
            </div>
            <button onClick={() => removeItem(idx)} disabled={items.length === 1}
              style={{ background: 'none', border: 'none', color: items.length === 1 ? '#ddd' : '#dc2626', fontSize: 20, cursor: 'pointer', padding: '6px 0', lineHeight: 1 }}>×</button>
          </div>
        ))}

        <button className="btn btn-outline btn-sm" onClick={addItem} style={{ marginTop: 8 }}>+ Add Row</button>

        {/* Totals */}
        <div style={{ marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <table style={{ minWidth: 300 }}>
              <tbody>
                <tr>
                  <td style={{ padding: '5px 16px 5px 0', color: '#666', textAlign: 'right' }}>Subtotal</td>
                  <td style={{ padding: '5px 0', fontWeight: 600, textAlign: 'right' }}>{sym}{fmt(subtotal)}</td>
                </tr>
                {isINR && (
                  <tr>
                    <td style={{ padding: '5px 16px 5px 0', color: '#666', textAlign: 'right' }}>GST ({taxRate}%)</td>
                    <td style={{ padding: '5px 0', fontWeight: 600, textAlign: 'right' }}>{sym}{fmt(taxAmount)}</td>
                  </tr>
                )}
                <tr style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: '8px 16px 5px 0', color: '#333', fontWeight: 600, textAlign: 'right' }}>Total</td>
                  <td style={{ padding: '8px 0 5px', fontWeight: 700, fontSize: 16, textAlign: 'right' }}>{sym}{fmt(total)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '5px 16px 5px 0', color: '#666', textAlign: 'right' }}>Amount Paid</td>
                  <td style={{ padding: '5px 0', textAlign: 'right' }}>
                    <input type="number" min="0" step="0.01" value={form.amount_paid} onChange={e => setF('amount_paid', e.target.value)}
                      style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 8px', width: 110, fontSize: 14, textAlign: 'right' }} />
                  </td>
                </tr>
                <tr style={{ borderTop: '2px solid #1a1a2e' }}>
                  <td style={{ padding: '8px 16px 0 0', color: '#1a1a2e', fontWeight: 700, textAlign: 'right' }}>Balance Due</td>
                  <td style={{ padding: '8px 0 0', fontWeight: 700, fontSize: 18, color: balanceDue > 0 ? '#e02d6f' : '#16a34a', textAlign: 'right' }}>{sym}{fmt(balanceDue)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Terms & Notes */}
      <div className="card">
        <div className="form-grid form-grid-2">
          <div className="form-group">
            <label>Terms</label>
            <textarea className="form-control" rows={2} value={form.terms} onChange={e => setF('terms', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Notes (internal / visible on invoice)</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Any additional notes..." />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 32 }}>
        <button className="btn btn-outline" onClick={() => navigate('/invoices')}>Cancel</button>
        <button className="btn btn-secondary" onClick={() => handleSave('draft')} disabled={saving}>{saving ? 'Saving...' : 'Save as Draft'}</button>
        <button className="btn btn-primary" onClick={() => handleSave('sent')} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}</button>
      </div>
    </div>
  );
}
